import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { Form } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import config from "../../Config";
import StudentPanelMenu from "./StudentPanelMenu";
import SupportFileAttachment from "../Support/SupportFileAttachment";
import SupportAttachmentViewer, {
    SupportAttachmentActions,
    hasSupportAttachment,
} from "../Support/SupportAttachmentViewer";
import {
  getStudentSupportApiPayload,
  getSupportApiErrorMessage,
} from "./studentSupportApiPayload";
import StudentSupportParentGuard from "./StudentSupportParentGuard";

function StudentSupportTicketDetail() {
  const { id } = useParams();
  const ticketId = parseInt(id, 10) || 0;
  const [replies, setReplies] = useState([]);
  const [ticket, setTicket] = useState({});
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [replySaved, setReplySaved] = useState(false);
  const [attachmentString, setAttachmentString] = useState("");
  const [attachmentImg, setAttachmentImg] = useState(null);
  const [showModalAttachment, setShowModalAttachment] = useState(false);

  const textareaRef = useRef(null);

  const autoResizeTextarea = (element) => {
    if (!element) return;

    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  };

  const loadTicket = useCallback(() => {
    if (!ticketId) {
      setLoadError("Invalid ticket.");
      return;
    }

    const payload = getStudentSupportApiPayload({ ID: ticketId });
    if (!payload.AccessKey || !payload.UserID) {
      setLoadError("Session expired. Please sign in again.");
      return;
    }

    setLoadError("");
    axios({
      method: "post",
      url: config.base_url + "Support/GetTicketByIDStudent",
      headers: { "Content-Type": "application/json" },
      data: payload,
    })
      .then((res) => {
        if (res.data.status_code === 1) {
          setReplies(res.data.ListofReplies || res.data.listofReplies || []);
          setTicket(res.data);
          const el = document.getElementById("student-chat-thread");
          if (el) {
            el.scrollTop = el.scrollHeight;
          }
        } else {
          const msg = res.data.status_message || "Could not load ticket.";
          setLoadError(msg);
          toast.error(msg);
        }
      })
      .catch((err) => {
        const msg = getSupportApiErrorMessage(err);
        setLoadError(msg);
        toast.error(msg);
      });
  }, [ticketId]);

  useEffect(() => {
    loadTicket();
  }, [loadTicket, replySaved]);

  const handleShowModalAttachment = (file) => {
    setAttachmentImg(file);
    setShowModalAttachment(true);
  };

  const submitReply = (event) => {
    event.preventDefault();
    if (!description.trim()) return;

    const payload = getStudentSupportApiPayload({
      ID: ticketId,
      Description: description.trim(),
      FileAttached: attachmentString,
    });
    if (!payload.AccessKey || !payload.UserID) {
      toast.error("Session expired. Please sign in again.");
      return;
    }

    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "Support/StudentReplyTicket",
      headers: { "Content-Type": "application/json" },
      data: payload,
    })
      .then((res) => {
        if (res.data.status_code === 1) {
          setDescription("");
          setAttachmentString("");

          if (textareaRef.current) {
            textareaRef.current.style.height = "48px";
          }
          setReplySaved((v) => !v);
          toast.success(res.data.status_message);
        } else {
          toast.error(res.data.status_message || "Could not send reply.");
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(getSupportApiErrorMessage(err));
      });
  };

  const isClosed = ticket.Status === "Closed";
  const canReply = !isClosed && !loadError;

  const renderReplies = () =>
    replies.map((item, index) => {
      const isStudent = Number(item.AdminID ?? item.adminID ?? 0) <= 0;

      const fullName = isStudent
        ? item.Member_Name
        : item.Admin_Name || "Support";

      const firstLetter = fullName?.charAt(0)?.toUpperCase();

      return (
        <li
          key={item.ID || index}
          className={`chat-item ${isStudent ? "student" : "admin"}`}
        >
          {/* AVATAR */}
          <div
            className={`chat-avatar ${isStudent ? "student-avatar" : "admin-avatar"
              }`}
            title={fullName}
          >
            {firstLetter}
          </div>

          {/* MESSAGE */}
          <div className="chat-text">
            <div
              className={`message-bubble ${isStudent ? "student-bubble" : "admin-bubble"
                }`}
            >
              <div
                className="message-content"
                dangerouslySetInnerHTML={{
                  __html: item.Description,
                }}
              />

              <span className="bubble-date">
                {moment(item.Created_Date).format(
                  "DD MMM YYYY • hh:mm A"
                )}
              </span>
            </div>

            {/* ATTACHMENT */}
            {hasSupportAttachment(item) && (
              <SupportAttachmentActions
                source={item}
                onView={(fileName) => {
                  setAttachmentImg(fileName);
                  setShowModalAttachment(true);
                }}
              />
            )}
          </div>
        </li>
      );
    });


  return (
    <StudentSupportParentGuard>
      <>
      <ToastContainer />
      <StudentPanelMenu />
      <div className="page-head">
        <div className="container">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/student-panel/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/student-panel/support/tickets">Support</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Ticket details
              </li>
            </ol>
          </nav>
          <div className="page-title">
            Ticket details{ticket.TicketType ? ` (${ticket.TicketType})` : ""}
          </div>
        </div>
      </div>
      <div className="container pb-4">
        {loadError ? (
          <div className="alert alert-warning">{loadError}</div>
        ) : null}
        <div className="ticket-card card mb-3">
          <div className="card-body ticket-card-body">

            {/* TOP INFO ROW */}
            <div className="ticket-meta">
              <div className="meta-item">
                <i className="ri-coupon-line"></i>
                <span>Ticket #{ticket.TicketID}</span>
              </div>

              <div className="meta-item">
                <i className="ri-calendar-2-fill"></i>
                <span>
                  {ticket.Dated
                    ? moment(ticket.Dated).format("DD MMM YYYY • hh:mm A")
                    : "-"}
                </span>
              </div>

              <div className="meta-item">
                <i className="ri-chat-poll-line"></i>
                <span>{ticket.NoofReplies} messages</span>
              </div>

              <div className={`ticket-status ${ticket.Status?.toLowerCase()}`}>
                {ticket.Status}
              </div>
            </div>

            {/* SUBJECT */}
            {ticket.Subject && (
              <div className="ticket-subject">
                {ticket.Subject}
              </div>
            )}

            {/* ATTACHMENT */}
            {hasSupportAttachment(ticket) && (
              <SupportAttachmentActions
                source={ticket}
                onView={handleShowModalAttachment}
                label="View original attachment"
              />
            )}

          </div>
        </div>
        <div className="message-wrap">

          <div id="student-chat-thread" className="chat-thread">
            <ul className="chat-list">{renderReplies()}</ul>
          </div>

          {/* REPLY */}
          {canReply ? (
            <Form onSubmit={submitReply}>
              <div className="reply-box">
                <SupportFileAttachment
                  compact
                  value={attachmentString}
                  onChange={setAttachmentString}
                  inputId="StudentReplyAttach"
                  disabled={loading}
                />
                <textarea
                  ref={textareaRef}
                  rows={1}
                  value={description}
                  className="send-reply"
                  placeholder="Type your reply..."
                  onChange={(e) => {
                    setDescription(e.target.value);
                    autoResizeTextarea(e.target);
                  }}
                />

                <button
                  type="submit"
                  disabled={loading || !description.trim()}
                  className="reply-button"
                >
                  {loading ? "Sending..." : "Send"}
                  <i className="ri-send-plane-2-fill"></i>
                </button>
              </div>
            </Form>
          ) : (
            <p className="closed-text">
              This ticket is closed.
            </p>
          )}
        </div>
      </div>

      <SupportAttachmentViewer
        show={showModalAttachment}
        fileName={attachmentImg}
        onHide={() => setShowModalAttachment(false)}
      />
      </>
    </StudentSupportParentGuard>
  );
}

export default StudentSupportTicketDetail;
